import { createClient } from '@supabase/supabase-js';
import { normalizeAuthConfig } from './auth-utils.js';

const raw=window.QADIATEY_CONFIG||{};
const authConfig=normalizeAuthConfig(raw);
const configured=Boolean(raw.supabaseUrl&&raw.supabasePublishableKey&&!String(raw.supabaseUrl).includes('YOUR_PROJECT'));
let client;

function fail(error){if(error)throw error;}
function normalizeRecord(item){return {...item,workspaceId:item.workspace_id,createdAt:item.created_at,updatedAt:item.updated_at,createdBy:item.created_by,updatedBy:item.updated_by};}

export function isConfigured(){return configured;}

export async function workspaceSummary(workspaceId){
  const modules=['matters','clients','contracts','requests','risks','tasks','deadlines'];
  const counts=await Promise.all(modules.map(async module=>{
    const {count,error}=await client.from('workspace_records').select('id',{count:'exact',head:true}).eq('workspace_id',workspaceId).eq('module',module);
    fail(error);return [module,count];
  }));
  const {count,error}=await client.from('documents').select('id',{count:'exact',head:true}).eq('workspace_id',workspaceId);fail(error);
  return {...Object.fromEntries(counts),documents:count};
}

export async function initPlatform(){
  if(!configured)return{configured:false,auth:authConfig};
  client=createClient(raw.supabaseUrl,raw.supabasePublishableKey,{auth:{persistSession:true,autoRefreshToken:true,detectSessionInUrl:true},global:{headers:{'x-application-name':'qadiatey-web'}}});
  return{configured:true,auth:authConfig};
}

export function observeAuth(callback){
  if(!configured){queueMicrotask(()=>callback(null,'INITIAL_SESSION'));return()=>{};}
  let active=true;
  client.auth.getSession().then(({data})=>{if(active)callback(data.session?.user||null,'INITIAL_SESSION');});
  const {data:{subscription}}=client.auth.onAuthStateChange((event,session)=>{if(active)callback(session?.user||null,event);});
  return()=>{active=false;subscription.unsubscribe();};
}

export async function getCurrentUser(){
  if(!configured)return null;
  const {data,error}=await client.auth.getUser();fail(error);return data.user||null;
}

export async function isPlatformAdmin(){
  const {data,error}=await client.rpc('is_platform_admin');fail(error);return data===true;
}

export async function platformAdminStats(){
  const {data,error}=await client.rpc('platform_admin_stats');fail(error);return data;
}

export async function accountAccessStatus(){
  const {data,error}=await client.rpc('account_access_status');fail(error);return data===true;
}

export async function setWorkspaceAiEnabled(workspaceId,enabled){
  const {data,error}=await client.rpc('set_workspace_ai_enabled',{p_workspace_id:workspaceId,p_enabled:enabled});fail(error);return data;
}

export async function prepareAiInput(workspaceId,facts,clientName='',opponentName=''){
  return call('prepareDraft',{workspaceId,facts,type:'ai-suggestion',clientName,opponentName});
}

export async function generateAiSuggestion(workspaceId,mode,cleanFacts){
  const {data,error}=await client.functions.invoke('ai-suggestion',{body:{workspaceId,mode,cleanFacts}});
  if(error){
    const detail=await error.context?.json?.().catch(()=>null);
    throw new Error(detail?.error||error.message);
  }
  return data;
}

export async function platformAdminDirectory(query='',userOffset=0,workspaceOffset=0){
  const {data,error}=await client.rpc('platform_admin_directory',{p_query:query,p_limit:25,p_user_offset:userOffset,p_workspace_offset:workspaceOffset});fail(error);return data;
}

export async function platformAdminSetAccountAccess(userId,paused){
  const {data,error}=await client.rpc('platform_admin_set_account_access',{p_user_id:userId,p_paused:paused});fail(error);return data;
}

export async function signInEmail(email,password,create=false,fullName=''){
  if(!configured)throw new Error('Supabase is not configured.');
  if(create&&!authConfig.emailSignupEnabled)throw new Error('Email signup is disabled.');
  const result=create?await client.auth.signUp({email,password,options:{emailRedirectTo:raw.redirectUrl||location.origin,data:{full_name:fullName.trim()}}}):await client.auth.signInWithPassword({email,password});
  fail(result.error);return result.data;
}

export async function signInProvider(provider){
  if(!configured)throw new Error('Supabase is not configured.');
  if(!authConfig.providers[provider])throw new Error('This sign-in provider is not enabled.');
  const selected=provider==='microsoft'?'azure':'google';
  const {data,error}=await client.auth.signInWithOAuth({provider:selected,options:{redirectTo:raw.redirectUrl||location.origin}});
  fail(error);return data;
}

export async function resetPassword(email){
  const {error}=await client.auth.resetPasswordForEmail(email,{redirectTo:`${raw.redirectUrl||location.origin}/?reset=1`});fail(error);
}
export async function updatePassword(password){
  if(!configured)throw new Error('Supabase is not configured.');
  const {data,error}=await client.auth.updateUser({password});fail(error);return data;
}
export async function listNotifications(workspaceId,max=30){
  const {data,error}=await client.from('notifications').select('*').or(`workspace_id.eq.${workspaceId},workspace_id.is.null`).order('created_at',{ascending:false}).limit(max);fail(error);
  return (data||[]).map(item=>({...item,workspaceId:item.workspace_id,readAt:item.read_at,createdAt:item.created_at}));
}
export async function markNotificationRead(id){
  const {data,error}=await client.rpc('mark_notification_read',{p_id:id});if(error)return false;return data;
}
async function allRows(table,order='created_at'){
  const rows=[];let start=0;
  for(;;){const {data,error}=await client.from(table).select('*').order(order,{ascending:true}).range(start,start+499);fail(error);rows.push(...(data||[]));if((data||[]).length<500)return rows;start+=500;}
}
export async function exportUserData(){
  const [profile,members,records,documents,notifications,timeEntries,invoices,payments,portalShares,matterEvents,matterAccess]=await Promise.all([
    client.from('profiles').select('id,email,display_name,active_workspace_id,workspace_ids,created_at,updated_at').maybeSingle(),
    allRows('workspace_members','joined_at'),allRows('workspace_records'),allRows('documents'),allRows('notifications'),
    allRows('time_entries'),allRows('invoices'),allRows('invoice_payments'),allRows('portal_shares'),
    allRows('matter_events'),allRows('matter_access','granted_at')
  ]);
  fail(profile.error);
  return {exportedAt:new Date().toISOString(),scope:'JSON metadata and records accessible to your account. Document file contents are NOT included.',profile:profile.data,memberships:members,records,documents,notifications,timeEntries,invoices,payments,portalShares,matterEvents,matterAccess};
}
export async function deletePersonalData(){
  throw new Error('Use leaveWorkspace; shared records cannot be deleted as personal data.');
}
export async function leaveWorkspace(workspaceId){const {data,error}=await client.rpc('leave_workspace',{p_workspace_id:workspaceId});fail(error);return data;}
export async function logout(){if(client){const {error}=await client.auth.signOut();fail(error);}}

export async function getProfile(uid){
  const {data,error}=await client.from('profiles').select('id,email,display_name,active_workspace_id,workspace_ids,updated_at').eq('id',uid).maybeSingle();fail(error);
  return data?{...data,displayName:data.display_name,activeWorkspaceId:data.active_workspace_id,workspaceIds:data.workspace_ids}:null;
}
export async function updateDisplayName(fullName){
  const name=fullName.trim();
  const {data,error}=await client.rpc('update_my_display_name',{p_display_name:name});
  if(!error)return data;
  // A deployed frontend can precede the profile-names migration. The existing
  // self-update policy still lets the user finish onboarding in that case.
  if(error.code!=='PGRST202')throw error;
  const user=await getCurrentUser();
  if(!user)throw new Error('Authentication required');
  const profile=await client.from('profiles').update({display_name:name}).eq('id',user.id).select('id').single();
  fail(profile.error);
  const auth=await client.auth.updateUser({data:{full_name:name}});
  fail(auth.error);
  return name;
}
export async function getWorkspace(workspaceId){const {data,error}=await client.from('workspaces').select('*').eq('id',workspaceId).maybeSingle();fail(error);return data?normalizeRecord(data):null;}
export async function listWorkspaces(){const {data,error}=await client.from('workspaces').select('id,name,type').order('name');fail(error);return data||[];}
export async function selectWorkspace(userId,workspaceId){const {data,error}=await client.from('profiles').update({active_workspace_id:workspaceId}).eq('id',userId).select('active_workspace_id').single();fail(error);return data.active_workspace_id;}
export async function listMembers(workspaceId){
  const {data,error}=await client.from('workspace_members').select('workspace_id,user_id,role,email,display_name,joined_at').eq('workspace_id',workspaceId).order('joined_at',{ascending:true});fail(error);
  return(data||[]).map(item=>({...item,workspaceId:item.workspace_id,userId:item.user_id,displayName:item.display_name,joinedAt:item.joined_at}));
}

const rpcMap={
  createWorkspace:['create_workspace',data=>({p_name:data.name,p_type:data.type})],
  createInviteCode:['create_invite_code',data=>({p_workspace_id:data.workspaceId})],
  joinWorkspace:['join_workspace',data=>({p_code:data.code})],
  setMemberRole:['set_member_role',data=>({p_workspace_id:data.workspaceId,p_user_id:data.uid,p_role:data.role})],
  removeMember:['remove_member',data=>({p_workspace_id:data.workspaceId,p_user_id:data.uid})],
  prepareDraft:['prepare_draft',data=>({p_workspace_id:data.workspaceId,p_facts:data.facts,p_type:data.type,p_client_name:data.clientName||'',p_opponent_name:data.opponentName||''})]
};
export async function call(name,payload={}){
  const spec=rpcMap[name];if(!spec)throw new Error(`Unknown operation: ${name}`);
  const {data,error}=await client.rpc(spec[0],spec[1](payload));fail(error);
  if(name==='createWorkspace')return{workspaceId:data};
  if(name==='joinWorkspace')return{workspaceId:data};
  return data;
}

export async function listRecords(workspaceId,collectionName){
  const table=collectionName==='documents'?'documents':'workspace_records';
  const rows=[];
  for(let start=0;;start+=500){
    let query=client.from(table).select('*').eq('workspace_id',workspaceId).order('updated_at',{ascending:false}).order('id',{ascending:false}).range(start,start+499);
    if(table==='workspace_records')query=query.eq('module',collectionName);
    const {data,error}=await query;fail(error);
    rows.push(...(data||[]));
    if((data||[]).length<500)break;
  }
  return rows.map(normalizeRecord);
}

export async function listFinanceRows(workspaceId,table){
  if(!['time_entries','invoices','invoice_payments'].includes(table))throw new Error('Unknown finance table');
  const rows=[];
  for(let start=0;;start+=500){
    const {data,error}=await client.from(table).select('*').eq('workspace_id',workspaceId).order('created_at',{ascending:false}).range(start,start+499);
    fail(error);rows.push(...(data||[]));if((data||[]).length<500)break;
  }
  return rows.map(normalizeRecord);
}

export async function listWorkspaceShares(workspaceId){
  const {data,error}=await client.from('portal_shares').select('id,document_id,recipient_email,created_at').eq('workspace_id',workspaceId).order('created_at',{ascending:false});fail(error);
  return data||[];
}

export async function listMySharedDocuments(){
  const uid=await currentUserId();
  const {data:shares,error}=await client.from('portal_shares').select('id,document_id,recipient_email,created_at').eq('recipient_user_id',uid).order('created_at',{ascending:false});fail(error);
  if(!shares?.length)return[];
  const {data:documents,error:documentError}=await client.from('documents').select('id,name,path,size,content_type').in('id',shares.map(share=>share.document_id));fail(documentError);
  const byId=new Map((documents||[]).map(document=>[document.id,document]));
  return shares.map(share=>({...share,document:byId.get(share.document_id)})).filter(share=>share.document);
}

export async function shareDocument(workspaceId,documentId,email){
  const {data,error}=await client.rpc('share_document_with_client',{p_workspace_id:workspaceId,p_document_id:documentId,p_recipient_email:email});fail(error);return data;
}

export async function revokeDocumentShare(shareId){
  const {data,error}=await client.rpc('revoke_document_share',{p_share_id:shareId});fail(error);return data;
}

async function currentUserId(){
  const {data,error}=await client.auth.getUser();fail(error);
  if(!data.user?.id)throw new Error('Authentication required.');
  return data.user.id;
}

export async function createTimeEntry(workspaceId,entry){
  const uid=await currentUserId();
  const {data,error}=await client.from('time_entries').insert({workspace_id:workspaceId,matter_id:entry.matterId||null,user_id:uid,work_date:entry.workDate,minutes:entry.minutes,hourly_rate:entry.hourlyRate,billable:entry.billable,description:entry.description,created_by:uid,updated_by:uid}).select().single();
  fail(error);return normalizeRecord(data);
}

export async function createInvoice(workspaceId,invoice){
  const uid=await currentUserId();
  const {data,error}=await client.from('invoices').insert({workspace_id:workspaceId,reference:invoice.reference,customer_name:invoice.customerName,amount:invoice.amount,due_at:invoice.dueAt||null,notes:invoice.notes||null,status:'draft',created_by:uid,updated_by:uid}).select().single();
  fail(error);return normalizeRecord(data);
}

export async function issueInvoice(workspaceId,id){
  const uid=await currentUserId();
  const {data,error}=await client.from('invoices').update({status:'issued',issued_at:new Date().toISOString().slice(0,10),updated_by:uid}).eq('workspace_id',workspaceId).eq('id',id).eq('status','draft').select().single();
  fail(error);return normalizeRecord(data);
}

export async function recordInvoicePayment(workspaceId,invoiceId,payment){
  const uid=await currentUserId();
  const {data,error}=await client.from('invoice_payments').insert({workspace_id:workspaceId,invoice_id:invoiceId,amount:payment.amount,paid_on:payment.paidOn,method:payment.method||null,reference:payment.reference||null,notes:payment.notes||null,created_by:uid}).select().single();
  fail(error);return normalizeRecord(data);
}
export async function documentUrl(path){const {data,error}=await client.storage.from('legal-documents').createSignedUrl(path,3600);fail(error);return data.signedUrl;}
export async function listMatterActivity(workspaceId,recordIds){
  const {data,error}=await client.from('audit_logs').select('*').eq('workspace_id',workspaceId).in('row_id',recordIds).order('occurred_at',{ascending:false}).limit(50);fail(error);return data||[];
}

export async function checkMatterConflict(workspaceId,name,identity=''){
  const {data,error}=await client.rpc('check_matter_conflict',{p_workspace_id:workspaceId,p_party_name:name,p_party_identity:identity});
  fail(error);return Number(data)||0;
}

export async function listMatterAccess(workspaceId,matterId){
  const {data,error}=await client.from('matter_access').select('user_id').eq('workspace_id',workspaceId).eq('matter_id',matterId);
  fail(error);return (data||[]).map(row=>row.user_id);
}

export async function setMatterAccess(workspaceId,matterId,userId,allowed){
  if(allowed){const uid=await currentUserId();const {error}=await client.from('matter_access').insert({workspace_id:workspaceId,matter_id:matterId,user_id:userId,granted_by:uid});fail(error);}
  else{const {error}=await client.from('matter_access').delete().eq('workspace_id',workspaceId).eq('matter_id',matterId).eq('user_id',userId);fail(error);}
}

export async function listMatterEvents(workspaceId,matterId){
  const {data,error}=await client.from('matter_events').select('*').eq('workspace_id',workspaceId).eq('matter_id',matterId).order('occurred_at',{ascending:false});
  fail(error);return data||[];
}

export async function createMatterEvent(workspaceId,matterId,event){
  const uid=await currentUserId();
  const {data,error}=await client.from('matter_events').insert({workspace_id:workspaceId,matter_id:matterId,event_type:event.type,title:event.title,details:event.details||'',occurred_at:event.occurredAt,created_by:uid}).select().single();
  fail(error);return data;
}

export async function createRecord(workspaceId,collectionName,data){
  const {data:userData}=await client.auth.getUser();const uid=userData.user?.id;if(!uid)throw new Error('Authentication required.');
  const {data:row,error}=await client.from('workspace_records').insert({workspace_id:workspaceId,module:collectionName,title:data.title,name:data.name,reference:data.reference,status:data.status,notes:data.notes,metadata:data.metadata||{},client_id:data.clientId||null,matter_id:data.matterId||null,assigned_to:data.assignedTo||null,is_confidential:data.isConfidential===true,created_by:uid,updated_by:uid}).select().single();fail(error);return normalizeRecord(row);
}
export async function updateRecord(workspaceId,collectionName,id,data){
  const {data:userData}=await client.auth.getUser();const uid=userData.user?.id;if(!uid)throw new Error('Authentication required.');
  const payload={...data,updated_by:uid};delete payload.clientId;delete payload.matterId;delete payload.assignedTo;delete payload.isConfidential;
  if('isConfidential'in data)payload.is_confidential=data.isConfidential===true;
  if('clientId'in data)payload.client_id=data.clientId||null;if('matterId'in data)payload.matter_id=data.matterId||null;if('assignedTo'in data)payload.assigned_to=data.assignedTo||null;
  const {data:row,error}=await client.from('workspace_records').update(payload).eq('id',id).eq('workspace_id',workspaceId).eq('module',collectionName).select().single();fail(error);return normalizeRecord(row);
}
export async function removeRecord(workspaceId,collectionName,id){const {data,error}=await client.from('workspace_records').delete().eq('id',id).eq('workspace_id',workspaceId).eq('module',collectionName).select('id');fail(error);if(!data?.length)throw new Error('Record was not deleted. Check your permissions.');}

export async function uploadDocument(workspaceId,file,matterId=null){
  const {data:userData}=await client.auth.getUser();const uid=userData.user?.id;if(!uid)throw new Error('Authentication required.');
  const safeName=file.name.replace(/[^\p{L}\p{N}._-]+/gu,'_');const path=`${workspaceId}/${uid}/${crypto.randomUUID()}-${safeName}`;
  const upload=await client.storage.from('legal-documents').upload(path,file,{contentType:file.type,upsert:false});fail(upload.error);
  const {data:row,error}=await client.from('documents').insert({workspace_id:workspaceId,name:file.name,path,size:file.size,content_type:file.type,matter_id:matterId,created_by:uid,updated_by:uid}).select().single();
  if(error){await client.storage.from('legal-documents').remove([path]);throw error;}
  return normalizeRecord(row);
}
export async function removeDocument(workspaceId,record){
  const {data,error}=await client.from('documents').select('id,path').eq('id',record.id).eq('workspace_id',workspaceId).maybeSingle();fail(error);
  if(!data)throw new Error('Document was not found.');
  const removal=await client.storage.from('legal-documents').remove([data.path]);fail(removal.error);
  const deleted=await client.from('documents').delete().eq('id',record.id).eq('workspace_id',workspaceId).select('id');fail(deleted.error);
  if(!deleted.data?.length)throw new Error('File was removed, but its record remains. Contact support.');
}
